import { Injectable } from '@angular/core';
import { TransactionType, CommType, OrderDuration, AccountType, OrderType, AllocationOrderWrapper } from '../../models/common/trading-common.model';
import { OptionTradingOrder } from '../../models/option/trading-option.model';
import { EquityTradingOrder } from './../../models/equity/trading-equity.model';

@Injectable()
export class TradingAllocationService {
    
    private getDefaultAllocationWrapper(): AllocationOrderWrapper {
        return {
            status: 'RcvNew',
            symbol: '',
            transaction: '',
            avgActType: '',
            orderQty: '',
            instruction: '',
            commAmt: '',
            commType: 'N/A',
            lmtPrice: '',
            stopLmtPrice: '',
            duration: '',
            dateTime: '',
            approvedBy: '',
            allocAccount: '',
            allocCategory: '',
            handInst: 'PE',
            secType: '',
            tid: '',
            orderFrom: '',
            cnfmOrdFrm: '',
            openCloseCd: ''
        };
    }

    private convertNullToEmpty(inputVal: any): string {
        let retVal: string;

        if (inputVal === null) {
            retVal = '';
        } else {
            retVal = inputVal.toString();
        }

        return retVal;
    }

    getCommissionAllocationParam(commType: CommType, transactionType:TransactionType):string  {
            if (commType === CommType.MUD ||
                commType === CommType.MUD_AUTO) {
                if (transactionType === TransactionType.BUY ||
                    transactionType === TransactionType.BUY_TO_COVER) {
                    return 'MARK-UP';
                } else {
                    return 'MARK-DOWN';
                }
            } else if (commType !== null) {
                return commType;
            } 
    }


    public getEquityAllocationParams(order: EquityTradingOrder, isRiaUser: boolean, isHedgeFundEnabled: boolean, isOrderSuccessfullySubmitted: boolean): AllocationOrderWrapper {
        // Instanciate the object with default values
        let allocateOrderWrapper = this.getDefaultAllocationWrapper();
        allocateOrderWrapper.secType = 'EQ';
        allocateOrderWrapper.symbol = order.quote.symId;
        allocateOrderWrapper.allocAccount = order.account.acctNum;
        allocateOrderWrapper.orderQty = this.convertNullToEmpty(order.ordQty);
        allocateOrderWrapper.lmtPrice = this.convertNullToEmpty(order.limitPrice);
        allocateOrderWrapper.stopLmtPrice = this.convertNullToEmpty(order.stopPrice);
        allocateOrderWrapper.dateTime = this.convertNullToEmpty(order.ordDatetimeStr);
        // It is just random/static number.
        // The .com's allocation screen is using the Transaction ID generated from PCFN and that is used to keep the data in the session/cache.
        // Passing some static value to avoid getting null pointer exception.
        allocateOrderWrapper.tid = '424324234';

        if (isOrderSuccessfullySubmitted) {
            allocateOrderWrapper.cnfmOrdFrm = 'CNFRMORDS';
            allocateOrderWrapper.allocCategory = '02';
        } else {
            allocateOrderWrapper.cnfmOrdFrm = '';
            allocateOrderWrapper.allocCategory = '01';
        }

        if (isRiaUser && isHedgeFundEnabled) {
            allocateOrderWrapper.orderFrom = 'HEDGEFUND';
        } else {
            allocateOrderWrapper.orderFrom = '';
        }

        if (order.commission !== null) {
            allocateOrderWrapper.commAmt = this.convertNullToEmpty(order.commission.commissionAmt);
            allocateOrderWrapper.commType = this.getCommissionAllocationParam(order.commission.commType,order.transactionType);
        }

        switch (order.duration) {
            case OrderDuration.DAY:
                allocateOrderWrapper.duration = '1';
                break;
            case OrderDuration.GTC:
                allocateOrderWrapper.duration = '2';
                break;
            case OrderDuration.OPG:
                allocateOrderWrapper.duration = 'O';
                break;
            case OrderDuration.IOC:
                allocateOrderWrapper.duration = '4';
                break;
            case OrderDuration.FOK:
                allocateOrderWrapper.duration = '3';
                break;
            case OrderDuration.GTD:
                allocateOrderWrapper.duration = '7';
                break;
            case OrderDuration.GTM:
                allocateOrderWrapper.duration = '8';
                break;
        }

        switch (order.transactionType) {
            case TransactionType.BUY:
            case TransactionType.BUY_TO_COVER:
                allocateOrderWrapper.transaction = '1';
                break;
            case TransactionType.SELL_FROM_PORTFOLIO:
            case TransactionType.SELL_ALL:
                allocateOrderWrapper.transaction = '2';
                break;
            case TransactionType.SELL_NOT_IN_ACCOUNT:
                allocateOrderWrapper.transaction = '3';
                break;
            case TransactionType.SELL_SHORT:
                allocateOrderWrapper.transaction = '4';
                break;
            case TransactionType.SHORT_SALE_EXEMPT:
                allocateOrderWrapper.transaction = '8';
                break;
        }

        switch (order.accountType) {
            case AccountType.COD:
                allocateOrderWrapper.avgActType = '0';
                break;
            case AccountType.CASH:
                allocateOrderWrapper.avgActType = '1';
                break;
            case AccountType.MARGIN:
                allocateOrderWrapper.avgActType = '2';
                break;
            case AccountType.SHORT:
                allocateOrderWrapper.avgActType = '3';
                break;
        }

        switch (order.orderType) {
            case OrderType.MARKET:
                allocateOrderWrapper.instruction = '1';
                break;
            case OrderType.LIMIT:
                allocateOrderWrapper.instruction = '2';
                break;
            case OrderType.STOP:
                allocateOrderWrapper.instruction = '3';
                break;
            case OrderType.STOP_LIMIT:
                allocateOrderWrapper.instruction = '4';
                break;
            case OrderType.EXECUTE_AT_CLOSE:
                allocateOrderWrapper.instruction = '5';
                break;
            case OrderType.WOW:
                allocateOrderWrapper.instruction = '7';
                break;
            case OrderType.TRAILING_STOP_DOLLAR:
                allocateOrderWrapper.instruction = '8';
                break;
            case OrderType.TRAILING_STOP_PCT:
                allocateOrderWrapper.instruction = '9';
                break;
            case OrderType.TRAILING_STOP_LIMIT_DOLLAR:
                allocateOrderWrapper.instruction = '10';
                break;
            case OrderType.TRAILING_STOP_LIMIT_PCT:
                allocateOrderWrapper.instruction = '11';
                break;
            case OrderType.OR_BETTER:
                allocateOrderWrapper.instruction = '6';
                break;
            default:
                allocateOrderWrapper.instruction = '';
                break;
        }

        return allocateOrderWrapper;
    }

    public getOptionAllocationParams(order: OptionTradingOrder, isRiaUser: boolean, isHedgeFundEnabled: boolean, isOrderSuccessfullySubmitted: boolean): AllocationOrderWrapper {
        // Instanciate the object with default values
        let allocateOrderWrapper = this.getDefaultAllocationWrapper();
        allocateOrderWrapper.secType = '02';
        allocateOrderWrapper.symbol = order.quote.symId;
        allocateOrderWrapper.allocAccount = order.account.acctNum;
        allocateOrderWrapper.orderQty = this.convertNullToEmpty(order.ordQty);
        allocateOrderWrapper.lmtPrice = this.convertNullToEmpty(order.limitPrice);
        allocateOrderWrapper.stopLmtPrice = this.convertNullToEmpty(order.stopPrice);
        allocateOrderWrapper.dateTime = this.convertNullToEmpty(order.ordDatetimeStr);
        // It is just random/static number.
        // The .com's allocation screen is using the Transaction ID generated from PCFN and that is used to keep the data in the session/cache.
        // Passing some static value to avoid getting null pointer exception.
        allocateOrderWrapper.tid = '432423424';

        if (isOrderSuccessfullySubmitted) {
            allocateOrderWrapper.cnfmOrdFrm = 'CNFRMORDS';
            allocateOrderWrapper.allocCategory = '02';
        } else {
            allocateOrderWrapper.cnfmOrdFrm = '';
            allocateOrderWrapper.allocCategory = '01';
        }

        if (isRiaUser && isHedgeFundEnabled) {
            allocateOrderWrapper.orderFrom = 'HEDGEFUND';
        } else {
            allocateOrderWrapper.orderFrom = '';
        }

        if (order.commission !== null) {
            allocateOrderWrapper.commAmt = this.convertNullToEmpty(order.commission.commissionAmt);
            allocateOrderWrapper.commType = this.getCommissionAllocationParam(order.commission.commType,order.transactionType);    
        }

        switch (order.duration) {
            case OrderDuration.DAY:
                allocateOrderWrapper.duration = '1';
                break;
            case OrderDuration.GTC:
                allocateOrderWrapper.duration = '2';
                break;
            case OrderDuration.OPG:
                allocateOrderWrapper.duration = 'O';
                break;
            case OrderDuration.IOC:
                allocateOrderWrapper.duration = '4';
                break;
            case OrderDuration.FOK:
                allocateOrderWrapper.duration = '3';
                break;
            case OrderDuration.GTD:
                allocateOrderWrapper.duration = '7';
                break;
            case OrderDuration.GTM:
                allocateOrderWrapper.duration = '8';
                break;
        }

        switch (order.transactionType) {
            case TransactionType.BUY_TO_OPEN:
            case TransactionType.BUY_TO_CLOSE:
            case TransactionType.BUY_CALL_TO_OPEN:
            case TransactionType.BUY_PUT_TO_OPEN:
            case TransactionType.BUY_CALL_TO_CLOSE:
            case TransactionType.BUY_PUT_TO_CLOSE:
                allocateOrderWrapper.transaction = '1';
                break;
            case TransactionType.SELL_TO_OPEN:
            case TransactionType.SELL_TO_CLOSE:
            case TransactionType.SELL_CALL_TO_OPEN:
            case TransactionType.SELL_PUT_TO_OPEN:
            case TransactionType.SELL_COVERED_CALLS:
            case TransactionType.CLOSE_EXISTING_CALL:
            case TransactionType.CLOSE_EXISTING_PUT:
                allocateOrderWrapper.transaction = '2';
                break;
        }

        switch (order.accountType) {
            case AccountType.COD:
                allocateOrderWrapper.avgActType = '0';
                break;
            case AccountType.CASH:
                allocateOrderWrapper.avgActType = '1';
                break;
            case AccountType.MARGIN:
                allocateOrderWrapper.avgActType = '2';
                break;
            case AccountType.SHORT:
                allocateOrderWrapper.avgActType = '3';
                break;
        }

        switch (order.orderType) {
            case OrderType.MARKET:
                allocateOrderWrapper.instruction = '1';
                break;
            case OrderType.LIMIT:
                allocateOrderWrapper.instruction = '2';
                break;
            case OrderType.STOP:
                allocateOrderWrapper.instruction = '3';
                break;
            case OrderType.STOP_LIMIT:
                allocateOrderWrapper.instruction = '4';
                break;
            case OrderType.EXECUTE_AT_CLOSE:
                allocateOrderWrapper.instruction = '5';
                break;
            case OrderType.WOW:
                allocateOrderWrapper.instruction = '7';
                break;
            case OrderType.TRAILING_STOP_DOLLAR:
                allocateOrderWrapper.instruction = '8';
                break;
            case OrderType.TRAILING_STOP_PCT:
                allocateOrderWrapper.instruction = '9';
                break;
            case OrderType.TRAILING_STOP_LIMIT_DOLLAR:
                allocateOrderWrapper.instruction = '10';
                break;
            case OrderType.TRAILING_STOP_LIMIT_PCT:
                allocateOrderWrapper.instruction = '11';
                break;
            case OrderType.OR_BETTER:
                allocateOrderWrapper.instruction = '6';
                break;
            default:
                allocateOrderWrapper.instruction = '';
                break;
        }

        switch (order.transactionType) {
            case TransactionType.BUY_CALL_TO_OPEN:
            case TransactionType.BUY_PUT_TO_OPEN:
            case TransactionType.SELL_CALL_TO_OPEN:
            case TransactionType.SELL_PUT_TO_OPEN:
                // Open
                allocateOrderWrapper.openCloseCd = 'O';
                break;
            case TransactionType.BUY_TO_CLOSE:
            case TransactionType.SELL_TO_CLOSE:
            case TransactionType.CLOSE_EXISTING_CALL:
            case TransactionType.CLOSE_EXISTING_PUT:
            case TransactionType.CLOSE_CALLS_OR_PUTS:
            case TransactionType.SELL_COVERED_CALLS:
                // Close
                allocateOrderWrapper.openCloseCd = 'C';
                break;
        }


        return allocateOrderWrapper;
    }
}
